import type { AdminUserDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Alert, Button, Dialog, ErrorAlert, Field, TextInput } from '../../shared/ui/index.js';
import { useSetUserPassword } from './adminHooks.js';

export function SetPasswordDialog({
  user,
  onClose,
}: {
  user: AdminUserDto;
  onClose: () => void;
}): ReactElement {
  const t = useTranslation();
  const setPassword = useSetUserPassword();
  const [password, setPassword_] = useState('');
  const fieldError = useFieldErrors(setPassword.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setPassword.mutate({ userId: user.id, input: { password } }, { onSuccess: onClose });
  };

  return (
    <Dialog title={t.setPasswordDialog.titleFor(user.username)} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={setPassword.error} />
        <Alert tone="warning">{t.setPasswordDialog.warning(user.username)}</Alert>

        <Field
          label={t.setPasswordDialog.newPasswordLabel}
          hint={t.setPasswordDialog.passwordHint}
          error={fieldError('password')}
        >
          {(props) => (
            <TextInput
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword_(event.target.value)}
              autoFocus
              required
            />
          )}
        </Field>

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={setPassword.isPending}>
            {t.setPasswordDialog.setPassword}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
